import React, { useMemo } from "react";
import DataTable from "react-data-table-component";
import { WarningFilled } from "@ant-design/icons";
import { getMaxDay, getBreedIdealSeries } from "./growthComparisonUtils";import { useTranslation } from 'react-i18next';


const ESTIMATED_COLOR = "#FF8C00";
const WARNING_COLOR = "#faad14";
// Matches the forecasting convention: actual/ideal below 0.95 = "Behind Target"
const DEVIATION_THRESHOLD = 0.05;

const GrowthComparisonTable = ({ data }) => {
  const { t } = useTranslation(['common', 'analytics', 'device', 'farm', 'batch', 'user']);
  const processed = useMemo(() => {
    if (!data || data.length === 0) return null;

    const maxDay = getMaxDay(data);
    const multiFarm = new Set(data.map((batch) => batch.farmName)).size > 1;

    // Day-keyed lookups so rows never rely on positional array assumptions.
    // Deviation compares each batch against its OWN breed's ideal for that day.
    const batchLookups = data.map((batch) => {
      const byDay = {};
      const flags = batch.interpolated || [];
      const ideals = batch.idealWeights || [];
      (batch.days || []).forEach((day, idx) => {
        const value = batch.weights[idx];
        const ideal = ideals[idx];
        let deviation = null;
        if (value != null && ideal != null && ideal > 0) {
          deviation = (value - ideal) / ideal;
        }
        byDay[day] = {
          value,
          interpolated: flags[idx] === true,
          belowIdeal: deviation != null && deviation < -DEVIATION_THRESHOLD,
          deviation,
        };
      });
      return byDay;
    });

    const breedIdeals = getBreedIdealSeries(data);
    const singleBreed = breedIdeals.size === 1;
    const idealColumns = [];
    breedIdeals.forEach(({ batch }, breed) => {
      const byDay = {};
      (batch.days || []).forEach((day, idx) => {
        byDay[day] = batch.idealWeights[idx];
      });
      idealColumns.push({
        label: singleBreed
          ? `Ideal Weight (${breed}) (kg)`
          : `Ideal — ${breed} (kg)`,
        byDay,
      });
    });

    const rows = [];
    for (let day = 0; day <= maxDay; day++) {
      rows.push({
        day,
        values: batchLookups.map((byDay) => byDay[day] || null),
        ideals: idealColumns.map((col) =>
          col.byDay[day] != null ? col.byDay[day] : null
        ),
      });
    }

    const batchLabels = data.map((batch) =>
      multiFarm && batch.farmName
        ? `${batch.farmName} — ${batch.batchName}`
        : batch.batchName
    );

    const hasEstimated = rows.some((row) =>
      row.values.some((cell) => cell && cell.interpolated && cell.value != null)
    );
    const hasWarnings = rows.some((row) =>
      row.values.some((cell) => cell && cell.belowIdeal)
    );

    return { rows, batchLabels, idealColumns, hasEstimated, hasWarnings };
  }, [data]);

  const columns = useMemo(() => {
    if (!processed) return [];

    const cols = [
      {
        name: t('common:time.day'),
        selector: (row) => row.day,
        sortable: true,
        width: "75px",
      },
    ];

    processed.batchLabels.forEach((label, i) => {
      cols.push({
        name: `${label} (kg)`,
        cell: (row) => {
          const cell = row.values[i];
          if (!cell || cell.value == null) return "--";
          const valueText = cell.interpolated ? (
            <span style={{ color: ESTIMATED_COLOR }}>
              ({cell.value.toFixed(3)}) *
            </span>
          ) : (
            cell.value.toFixed(3)
          );
          if (!cell.belowIdeal) return valueText;
          const pctBelow = (-cell.deviation * 100).toFixed(1);
          return (
            <span title={`${pctBelow}% below ideal weight`}>
              {valueText}
              <WarningFilled
                style={{ color: WARNING_COLOR, marginLeft: 4, fontSize: 12 }}
              />
            </span>
          );
        },
        sortable: true,
        sortFunction: (a, b) =>
          ((a.values[i] && a.values[i].value) || 0) -
          ((b.values[i] && b.values[i].value) || 0),
      });
    });

    processed.idealColumns.forEach((col, i) => {
      cols.push({
        name: col.label,
        cell: (row) =>
          row.ideals[i] != null ? row.ideals[i].toFixed(3) : "--",
        sortable: true,
        sortFunction: (a, b) => (a.ideals[i] || 0) - (b.ideals[i] || 0),
      });
    });

    return cols;
  }, [processed, t]);

  if (!processed) return null;

  return (
    <div style={{ marginTop: 20 }}>
      <DataTable
        columns={columns}
        data={processed.rows}
        defaultSortField="day"
        pagination
        paginationPerPage={25}
        paginationRowsPerPageOptions={[10, 25, 50, 100]}
        dense
        compact
        highlightOnHover
        selectableRowsHighlight
      />
      {processed.hasEstimated && (
        <p style={{ color: ESTIMATED_COLOR, fontSize: 12, marginTop: 5 }}>
          *: Estimated Weight
        </p>
      )}
      {processed.hasWarnings && (
        <p style={{ fontSize: 12, marginTop: 5 }}>
          <WarningFilled style={{ color: WARNING_COLOR, marginRight: 4 }} />
          {t('analytics:compare.belowIdealNote')}
        </p>
      )}
    </div>
  );
};

export default GrowthComparisonTable;
